/**
 * Classify Mapbox map load errors (GL JS `error` event / RN `onMapLoadingError`).
 * Per-tile composite/satellite misses are recoverable; style/auth failures are reportable.
 */

export const MAP_STYLE_LOAD_TIMEOUT_MS = 8_000

export type MapLoadFailureCause = 'style_error' | 'style_load_timeout'

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object'
}

/** RN wraps native payloads; GL JS puts tile/source on the ErrorEvent, not always on `error`. */
function collectPayloads(error: unknown): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = []
  const seen = new Set<unknown>()
  const add = (value: unknown) => {
    if (!isRecord(value) || seen.has(value)) return
    seen.add(value)
    out.push(value)
  }
  add(error)
  if (isRecord(error)) {
    add(error.nativeEvent)
    add(error.payload)
    if (isRecord(error.nativeEvent)) add(error.nativeEvent.payload)
    add(error.error)
  }
  return out
}

function mapErrorMessage(error: unknown, payloads: Record<string, unknown>[]): string {
  const parts: string[] = []
  if (typeof error === 'string') parts.push(error)
  if (error instanceof Error) parts.push(error.message)
  for (const e of payloads) {
    if (typeof e.message === 'string') parts.push(e.message)
    if (typeof e.error === 'string') parts.push(e.error)
    if (e.error instanceof Error) parts.push(e.error.message)
  }
  return parts.join(' ')
}

/**
 * One Mapbox tile failed to fetch. The style is fine and the map still renders;
 * these must never go to Bugsnag (they arrive in the thousands).
 */
export function isPerTileMapLoadingError(error: unknown): boolean {
  const payloads = collectPayloads(error)
  if (/failed to load tile/i.test(mapErrorMessage(error, payloads))) return true
  for (const e of payloads) {
    if (e.tileId != null || e.tile != null) return true
    if (e.type === 'tile' || e.errorType === 'tile') return true
  }
  return false
}

/** Offline / DNS failures — expected when the device has no network; still report for ER reliability. */
export function isOfflineOrDnsMapLoadingError(error: unknown): boolean {
  if (isPerTileMapLoadingError(error)) return false
  if (!error || typeof error !== 'object') return false
  const payloads = collectPayloads(error)
  const msg = mapErrorMessage(error, payloads)
  return /unable to resolve host|no address associated with hostname|network.*(fail|error|unreachable)|failed to connect|enotfound|eai_again|offline|timed?\s*out/i.test(
    msg,
  )
}

export function isStyleLevelMapLoadingError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return true
  if (isPerTileMapLoadingError(error)) return false
  if (isOfflineOrDnsMapLoadingError(error)) return false
  const payloads = collectPayloads(error)
  const status = payloads.reduce<number | null>((found, e) => {
    if (found != null) return found
    return typeof e.status === 'number' ? e.status : null
  }, null)
  if (status === 401 || status === 403 || status === 404) return true
  const msg = mapErrorMessage(error, payloads)
  return /style|stylesheet|sprite|glyphs|not found|unauthorized|forbidden|source/i.test(msg)
}

/** Short detail string from a Mapbox load error payload (Bugsnag / logs). */
export function summarizeMapLoadingError(error: unknown): string {
  if (!error || typeof error !== 'object') {
    return typeof error === 'string' ? error : String(error ?? 'unknown')
  }
  const payloads = collectPayloads(error)
  const status = payloads.reduce<number | null>((found, e) => {
    if (found != null) return found
    return typeof e.status === 'number' ? e.status : null
  }, null)
  const msg = mapErrorMessage(error, payloads)
  const parts: string[] = []
  if (status != null) parts.push(`HTTP ${status}`)
  if (msg.trim()) parts.push(msg.trim())
  const sourceId = payloads.find((e) => e.sourceId != null)?.sourceId
  if (sourceId != null && !parts.length) parts.push(`source=${String(sourceId)}`)
  return parts.length ? parts.join(' — ') : JSON.stringify(error)
}

/** Bugsnag/email-friendly one-line summary for reportable style-level failures. */
export function formatStyleLevelMapLoadFailureMessage(params: {
  mapRole?: string | null
  styleUrl?: string | null
  cause: MapLoadFailureCause
  error?: unknown
  timeoutMs?: number
}): string {
  const role = params.mapRole?.trim() || 'unknown'
  const url = params.styleUrl?.trim() || '(no style URL)'
  if (params.cause === 'style_load_timeout') {
    const seconds = Math.round((params.timeoutMs ?? MAP_STYLE_LOAD_TIMEOUT_MS) / 1000)
    return `Map style load timeout (${seconds}s) [${role}]: ${url}`
  }
  const detail =
    params.error != null ? summarizeMapLoadingError(params.error) : 'unknown error'
  return `Map style load failed [${role}]: ${url} — ${detail}`
}

/** Non-fatal per-tile miss — log locally only (both platforms). */
export function logIntermittentMapTileLoadMiss(
  styleUrl: string | null | undefined,
  error: unknown,
): void {
  console.debug(
    '[Map] Intermittent Mapbox tile load miss (non-fatal):',
    styleUrl ?? '',
    JSON.stringify(error),
  )
}
